import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";

export type AiDefaultMode = "fast" | "economy";

export interface BrandAiConfig {
  llmModel: string | null;
  imageModel: string | null;
  videoModel: string | null;
  defaultMode: AiDefaultMode | null;
}

export interface AiModelEntry {
  /** Registry name — the value stored in BrandAiConfig. */
  id: string;
  name: string;
  kind: "llm" | "image" | "video";
  /** Price hint for the dropdown, e.g. "$0.3024/s". */
  costLabel: string | null;
}

/** Shape of `GET /ai/models` (packages/ai listModels()). */
interface RegistryModel {
  name: string;
  type: "llm" | "image" | "video";
  inputCostPerMTok?: number;
  outputCostPerMTok?: number;
  costPerImage?: number;
  costPerSecond?: number;
}

export function toAiModelEntry(m: RegistryModel): AiModelEntry {
  let costLabel: string | null = null;
  if (m.type === "llm" && m.inputCostPerMTok != null && m.outputCostPerMTok != null) {
    costLabel = `$${m.inputCostPerMTok} / $${m.outputCostPerMTok} per 1M tokens`;
  } else if (m.type === "image" && m.costPerImage != null) {
    costLabel = `$${m.costPerImage}/image`;
  } else if (m.type === "video" && m.costPerSecond != null) {
    costLabel = `$${m.costPerSecond}/s`;
  }
  return { id: m.name, name: m.name, kind: m.type, costLabel };
}

export function useAiConfig(brandId: string) {
  return useQuery({
    queryKey: ["ai-config", brandId],
    queryFn: () => apiFetch<BrandAiConfig>(`/brands/${brandId}/ai-config`),
    enabled: !!brandId,
  });
}

export function useAiModels() {
  return useQuery({
    queryKey: ["ai-models"],
    // The API returns registry entries ({ name, type, cost… }); the page
    // needs { id, kind, costLabel }. Reading `kind` off the raw entries left
    // every dropdown empty.
    queryFn: async () => (await apiFetch<RegistryModel[]>(`/ai/models`)).map(toAiModelEntry),
    staleTime: 5 * 60_000,
  });
}

export function useUpdateAiConfig(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<BrandAiConfig>) =>
      apiFetch<BrandAiConfig>(`/brands/${brandId}/ai-config`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ai-config", brandId] }),
  });
}
